import { useState } from 'react';
import { useTranslation } from '@/contexts/i18n-context';
import { useGetEncountersPhotos } from '@/api/get-photos';
import { useGetEncountersFiles } from '@/api/get-files';
import { SMALL_IMAGE_SIZE } from '@/util/constants';
import { BodyLocationType, ImageFileDto } from '@/api/api.schemas';
import { Loader } from '@/components/loaders/loader';
import { DataRow } from '@/components/encounter/review/expandable-info';
import { FileItem } from '@/components/uploads/file/file-upload';
import { Button } from '@/components/button';
import clsx from 'clsx';
import styles from './condition-section-components.module.css';
import reviewStyles from '@/styles/review-page.module.css';
import { useEncounter } from '@/contexts/encounter-context';
import { Img } from '../img';

const translationBase = 'visit.review';
const UNSPECIFIED_KEY = 'unspecified';

type DisplayImage = Omit<ImageFileDto, 'bodyLocation'> & {
  bodyLocation: BodyLocationType | 'unspecified';
};

export function Photos() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const {
    data: photosResponse,
    isFetching,
    error,
  } = useGetEncountersPhotos(encounter.id, {
    width: SMALL_IMAGE_SIZE,
    height: SMALL_IMAGE_SIZE,
  });

  if (isFetching) {
    return <Loader type="small" />;
  }

  if (error || !photosResponse) {
    return (
      <DataRow title={t(`${translationBase}.condition.photos`)}>
        <p>{t(`${translationBase}.condition.errorPhotos`)}</p>
      </DataRow>
    );
  }

  const photos: DisplayImage[] = photosResponse.files.map((file) => ({
    ...file,
    bodyLocation: file.bodyLocation ?? UNSPECIFIED_KEY,
  }));

  return (
    <FileWrapper
      filesWrapper={photos.map((photo) => (
        <div
          key={photo.id}
          className={styles.photoContainer}
          data-testid="review-condition-photo-row"
        >
          <Img
            key={photo.id}
            src={photo.imageSrc}
            alt={photo.description ?? photo.bodyLocation}
            className={styles.photo}
          />
          <div>
            <p className="body2" data-testid="review-condition-photo-caption">
              {t(`bodyLocation.${photo.bodyLocation}`)}
            </p>
            <p className={clsx('body-4', reviewStyles.description)}>{photo.description}</p>
          </div>
        </div>
      ))}
    />
  );
}

export function Files() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { data: files, isFetching, error } = useGetEncountersFiles(encounter.id);

  if (isFetching) {
    return <Loader type="small" />;
  }

  if (error || !files) {
    return (
      <DataRow title={t(`${translationBase}.condition.files`)}>
        <p>{t(`${translationBase}.condition.errorFiles`)}</p>
      </DataRow>
    );
  }

  if (files.files.length === 0) {
    return null;
  }

  const fileItems = files.files.map((file) => ({
    name: file.description,
    tempId: file.id.toString(),
    status: 'uploaded' as const,
  }));

  return (
    <FileWrapper
      filesWrapper={fileItems.map((file) => (
        <FileItem key={file.tempId} file={file} />
      ))}
    />
  );
}

interface FileWrapperProps {
  filesWrapper: React.ReactNode[];
}

function FileWrapper({ filesWrapper }: FileWrapperProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const maxCount = 4;

  const displayFiles = isExpanded ? filesWrapper : filesWrapper.slice(0, maxCount);

  return (
    <div className={styles.photosContainer} data-testid="review-condition-photo-gallery">
      {displayFiles}
      {filesWrapper.length > maxCount && (
        <Button
          className={styles.photoButton}
          variant="link"
          onClick={() => setIsExpanded(!isExpanded)}
          rightIcon={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        >
          {isExpanded
            ? t(`${translationBase}.condition.collapseAll`)
            : t(`${translationBase}.condition.expandAll`)}
        </Button>
      )}
    </div>
  );
}
